"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { useRouter } from "next/navigation";

export default function CTASection() {
    const router = useRouter();
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="relative py-24 px-6 overflow-hidden">
            <div className="max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.8 }}
                    className="relative rounded-[2.5rem] bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-700 p-8 sm:p-16 text-center overflow-hidden shadow-2xl shadow-indigo-500/20"
                >
                    {/* Background patterns */}
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-400 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />
                    </div>

                    <div className="relative z-10 max-w-2xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold uppercase tracking-wider mb-6"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Ready to Scale?
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.3 }}
                            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6"
                        >
                            Start sending campaigns that actually reach the inbox.
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.4 }}
                            className="text-lg text-indigo-100 mb-10 leading-relaxed"
                        >
                            Join hundreds of organizations using iComm to manage their contacts 
                            and deliver high-converting email sequences.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.5 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-4"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => router.push("/contact")}
                                className="group px-8 py-4 rounded-2xl bg-white text-indigo-600 font-black text-sm shadow-xl shadow-black/10 hover:shadow-white/20 transition-all flex items-center gap-2"
                            >
                                Get Started Now
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </motion.button>
                            
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => router.push("/login")}
                                className="px-8 py-4 rounded-2xl bg-indigo-500/20 backdrop-blur-md border border-white/20 text-white font-black text-sm hover:bg-white/10 transition-all"
                            >
                                Contact Sales
                            </motion.button>
                        </motion.div>
                    </div>

                    {/* Floating elements */}
                    <motion.div
                        animate={{ y: [-10, 10, -10], rotate: [0, 5, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-10 right-10 hidden lg:block w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md border border-white/20"
                    />
                    <motion.div
                        animate={{ y: [10, -10, 10], rotate: [0, -5, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute bottom-10 left-10 hidden lg:block w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20"
                    />
                </motion.div>
            </div>
        </section>
    );
}
